import { useState } from "react";
import { ChevronRight, ChevronDown, Building2, Briefcase, FolderKanban, LayoutDashboard, ListChecks, MapPinned, Boxes } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";

export const Sidebar = ({
  isOpen,
  hierarchy,
  selectedCompanyId,
  selectedBusinessId,
  selectedProjectId,
  onSelectCompany,
  onSelectBusiness,
  onSelectProject,
  onSelectSite,
  onGoToDashboard,
  onGoToOps,
  onGoToTasks,
  isTaskView,
  isOpsView,
  selectedSiteId,
}) => {
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [expandedBusinesses, setExpandedBusinesses] = useState({});

  return (
    <aside className={`sidebar ${isOpen ? "sidebar-open" : ""}`} data-testid="sidebar">
      <div className="px-4 py-5 border-b border-zinc-200">
        <h1 className="font-heading text-lg font-black uppercase tracking-tight text-zinc-900">Batsho Manager</h1>
        <p className="text-xs text-zinc-500 mt-1">Portfolio workspace</p>
      </div>
      <div className="px-3 py-3 border-b border-zinc-200">
        <button
          onClick={onGoToDashboard}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${!selectedCompanyId && !selectedBusinessId && !selectedProjectId ? "bg-blue-50 text-blue-900 border-r-2 border-blue-600" : "text-zinc-600 hover:bg-zinc-100"}`}
        >
          <LayoutDashboard size={16} />
          <span>Dashboard</span>
        </button>
        <button
          onClick={onGoToTasks}
          className={`mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${isTaskView ? "bg-blue-50 text-blue-900 border-r-2 border-blue-600" : "text-zinc-600 hover:bg-zinc-100"}`}
        >
          <ListChecks size={16} />
          <span>Tasks</span>
        </button>
        <button
          onClick={onGoToOps}
          className={`mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${isOpsView ? "bg-blue-50 text-blue-900 border-r-2 border-blue-600" : "text-zinc-600 hover:bg-zinc-100"}`}
        >
          <Boxes size={16} />
          <span>Operations</span>
        </button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-1">
          {hierarchy.length === 0 ? (
            <div className="px-3 py-8 text-center text-zinc-400 text-sm">
              <p>No companies yet</p>
              <p className="text-xs mt-1">Click "+ Add Company" to start</p>
            </div>
          ) : (
            hierarchy.map((company) => (
              <div key={company.id} className="space-y-0.5">
                <div className={`tree-item ${selectedCompanyId === company.id && !selectedBusinessId ? "active" : ""}`}>
                  <button onClick={() => setExpandedCompanies((prev) => ({ ...prev, [company.id]: !prev[company.id] }))} className="p-0.5 hover:bg-zinc-300 rounded-sm">
                    {expandedCompanies[company.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <Briefcase size={14} className="text-zinc-500" />
                  <span className="flex-1 truncate cursor-pointer" onClick={() => onSelectCompany(company.id)}>{company.name}</span>
                </div>
                {expandedCompanies[company.id] && (
                  <div className="tree-nested-1">
                    {(company.businesses || []).length === 0 ? <div className="px-2 py-2 text-xs text-zinc-400 italic">No businesses</div> : (
                      company.businesses.map((business) => (
                        <div key={business.id} className="space-y-0.5">
                          <div className={`tree-item ${selectedBusinessId === business.id && !selectedProjectId ? "active" : ""}`}>
                            <button onClick={() => setExpandedBusinesses((prev) => ({ ...prev, [business.id]: !prev[business.id] }))} className="p-0.5 hover:bg-zinc-300 rounded-sm">
                              {expandedBusinesses[business.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                            <Building2 size={14} className="text-zinc-500" />
                            <span className="flex-1 truncate cursor-pointer" onClick={() => onSelectBusiness(business.id, company.id)}>{business.name}</span>
                          </div>
                          {expandedBusinesses[business.id] && (
                            <div className="tree-nested-2">
                              {(business.ops_sites || []).map((site) => (
                                <div key={site.id} className={`tree-item ${selectedSiteId === site.id ? "active" : ""}`} onClick={() => onSelectSite(site.id, business.id, company.id)}>
                                  <MapPinned size={14} className="text-zinc-500 ml-4" />
                                  <span className="flex-1 truncate">{site.name}</span>
                                </div>
                              ))}
                              {(business.projects || []).length === 0 ? <div className="px-2 py-2 text-xs text-zinc-400 italic">No projects</div> : (
                                business.projects.map((project) => (
                                  <div key={project.id} className={`tree-item ${selectedProjectId === project.id ? "active" : ""}`} onClick={() => onSelectProject(project.id, business.id, company.id)}>
                                    <FolderKanban size={14} className="text-zinc-500 ml-4" />
                                    <span className="flex-1 truncate">{project.name}</span>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </ScrollArea>
      <div className="px-4 py-3 border-t border-zinc-200 bg-zinc-50">
        <p className="text-xs text-zinc-400">{hierarchy.length} {hierarchy.length === 1 ? "company" : "companies"}</p>
      </div>
    </aside>
  );
};
